import { useEffect, useRef, useState, type DragEvent } from 'react';
import { useParams } from 'react-router';
import { useAuthStore } from '../auth/authStore.js';
import {
  EMPTY_PENDING,
  EMPTY_TRANSCRIPT,
  EMPTY_TYPING,
  presenceOf,
  useChatStore,
} from '../chat/chatStore.js';
import { useDirectory } from '../chat/directoryStore.js';
import { counterpartId, memberLabel, typingLabel } from '../chat/labels.js';
import { acknowledgeLatestRead } from '../chat/receiptAcks.js';
import { useUploadStore } from '../files/uploadStore.js';
import { useSocket } from '../socket/SocketProvider.js';
import { AddMembersDialog } from '../components/AddMembersDialog.js';
import { DeleteChatDialog } from '../components/DeleteChatDialog.js';
import { AttachButton, UploadTray } from '../components/FileUploads.js';
import { Icon } from '../components/Icon.js';
import { MessageComposer } from '../components/MessageComposer.js';
import { MessageList } from '../components/MessageList.js';
import { Avatar } from '../components/Avatar.js';
import { EmptyState } from '../components/EmptyState.js';
import { Skeleton } from '../components/Skeleton.js';
import type { PresenceStatus } from '@orbit-sync/shared-types';

/** Stable empty list, for the render before a channel is known. */
const EMPTY_IDS: string[] = [];

export function ChatPage() {
  const { channelId } = useParams<{ channelId: string }>();
  const currentUserId = useAuthStore((state) => state.user?.id ?? null);

  const channel = useChatStore((state) =>
    channelId ? state.channels.find((item) => item.id === channelId) : undefined,
  );
  const transcript = useChatStore((state) =>
    channelId ? state.transcripts[channelId] : undefined,
  );
  // Stable fallbacks: a literal here would be a new reference on every render.
  const pending = useChatStore((state) =>
    channelId ? (state.pending[channelId] ?? EMPTY_PENDING) : EMPTY_PENDING,
  );
  const presence = useChatStore((state) => state.presence);
  const channelsStatus = useChatStore((state) => state.channelsStatus);
  const loadHistory = useChatStore((state) => state.loadHistory);
  const setActiveChannel = useChatStore((state) => state.setActiveChannel);
  // Declared before the early returns below: hooks cannot be called conditionally.
  const directory = useDirectory(channel?.memberIds ?? EMPTY_IDS);
  const { socket } = useSocket();
  // A string, so the selector is stable: it changes only when a message lands.
  const newestMessageId = useChatStore((state) =>
    channelId ? (state.transcripts[channelId]?.items.at(-1)?.id ?? null) : null,
  );
  const typing = useChatStore((state) =>
    channelId ? (state.typing[channelId] ?? EMPTY_TYPING) : EMPTY_TYPING,
  );
  const startUploads = useUploadStore((state) => state.start);
  const [dragging, setDragging] = useState(false);
  // dragenter/dragleave fire for every child crossed; counting them is what
  // tells "left the conversation" from "moved onto a bubble".
  const dragDepth = useRef(0);
  const [dialog, setDialog] = useState<'add' | 'leave' | null>(null);

  useEffect(() => {
    if (channelId) {
      void loadHistory(channelId);
    }
  }, [channelId, loadHistory]);

  // Marks the open channel read, and clears the mark on the way out so messages
  // arriving after the user navigates away are counted again.
  useEffect(() => {
    setActiveChannel(channelId ?? null);

    return () => setActiveChannel(null);
  }, [channelId, setActiveChannel]);

  // Read receipts: whatever is on screen in a visible tab has been read. Runs
  // when the conversation opens, when its history lands, when a new message
  // arrives - and below, when the tab comes back into view.
  useEffect(() => {
    if (socket && channelId) {
      acknowledgeLatestRead(socket, channelId, currentUserId);
    }
  }, [socket, channelId, currentUserId, newestMessageId]);

  useEffect(() => {
    if (!socket || !channelId) {
      return;
    }

    function onVisible(): void {
      if (socket && channelId && document.visibilityState === 'visible') {
        acknowledgeLatestRead(socket, channelId, currentUserId);
        // Messages that arrived while the tab was hidden counted as unread.
        setActiveChannel(channelId);
      }
    }

    document.addEventListener('visibilitychange', onVisible);

    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [socket, channelId, currentUserId, setActiveChannel]);

  // Paste files (a screenshot, files copied in Explorer/Finder) anywhere on the
  // page and they upload at once. On the document rather than the composer, so
  // it works without first clicking into the text box. Text pastes, and pastes
  // inside a dialog, are left alone.
  useEffect(() => {
    if (!channelId || !channel) {
      return;
    }

    function onPaste(event: ClipboardEvent): void {
      const files = [...(event.clipboardData?.files ?? [])];

      if (files.length === 0 || (event.target as Element | null)?.closest?.('dialog')) {
        return;
      }

      event.preventDefault();
      startUploads(channelId as string, files.map(namePasted));
    }

    document.addEventListener('paste', onPaste);

    return () => document.removeEventListener('paste', onPaste);
  }, [channelId, channel, startUploads]);

  if (!channelId) {
    return (
      <EmptyState
        icon="messagesEmpty"
        title="Pick a conversation"
        description="Choose someone from the list to open a conversation, or start a new one. Messages arrive in real time, and you can drop files straight into a conversation."
      />
    );
  }

  // The channel list may still be loading on a deep link into a conversation.
  if (!channel) {
    if (channelsStatus === 'loading' || channelsStatus === 'idle') {
      return (
        <section className="chat">
          <header className="chat-header">
            <Skeleton width={36} height={36} circle />
            <div className="chat-header-body">
              <Skeleton width={150} height={13} />
            </div>
          </header>
        </section>
      );
    }

    return (
      <EmptyState
        icon="slash"
        title="Conversation not found"
        description="That conversation does not exist, or you are not a member of it."
      />
    );
  }

  const isDirect = channel.type === 'direct';
  const isSaved = channel.type === 'saved';
  const isGroup = channel.type === 'group';
  const other = counterpartId(channel, currentUserId);
  const title = isDirect && other ? memberLabel(other, currentUserId, directory) : channel.name;
  const presenceStatus = isDirect && other ? presenceOf(presence, other) : null;
  const typingNow = typingLabel(typing, currentUserId, directory, isDirect);

  const carriesFiles = (event: DragEvent): boolean => event.dataTransfer.types.includes('Files');

  // Drop a file anywhere on the conversation and it uploads straight away.
  const dropHandlers = {
    onDragEnter(event: DragEvent<HTMLElement>): void {
      if (carriesFiles(event)) {
        event.preventDefault();
        dragDepth.current += 1;
        setDragging(true);
      }
    },
    onDragOver(event: DragEvent<HTMLElement>): void {
      if (carriesFiles(event)) {
        // Without this the browser opens the file instead of dropping it here.
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
      }
    },
    onDragLeave(event: DragEvent<HTMLElement>): void {
      if (carriesFiles(event)) {
        dragDepth.current = Math.max(0, dragDepth.current - 1);

        if (dragDepth.current === 0) {
          setDragging(false);
        }
      }
    },
    onDrop(event: DragEvent<HTMLElement>): void {
      if (!carriesFiles(event)) {
        return;
      }

      event.preventDefault();
      dragDepth.current = 0;
      setDragging(false);

      const files = [...event.dataTransfer.files];

      if (files.length > 0) {
        startUploads(channel.id, files);
      }
    },
  };

  return (
    <section className="chat" {...dropHandlers}>
      {dragging ? (
        <div className="drop-overlay" aria-hidden="true">
          <Icon name="upload" size={28} />
          <strong>
            {isSaved ? 'Drop to keep in Saved messages' : `Drop to share with ${title}`}
          </strong>
          <span>
            {isSaved
              ? 'Download it from your other devices until it expires'
              : 'Files are sent as soon as you let go'}
          </span>
        </div>
      ) : null}

      <header className="chat-header">
        <Avatar
          name={title}
          id={other ?? channel.id}
          group={isGroup}
          saved={isSaved}
          presence={isDirect && other ? presenceStatus : undefined}
        />

        <div className="chat-header-body">
          <h1 className="truncate">{title}</h1>
          <p className="chat-subtitle truncate">
            {isSaved ? (
              <span>Notes, kept messages and files - on every device you sign in on</span>
            ) : typingNow ? (
              <span className="chat-typing">{typingNow}</span>
            ) : isDirect ? (
              <>
                <span>Direct message</span>
                <span className="sep">·</span>
                <span>{presenceLabel(presenceStatus)}</span>
              </>
            ) : (
              <>
                <span>{channel.memberIds.length} members</span>
                <span className="sep">·</span>
                <span className="truncate">
                  {channel.memberIds
                    .map((id) => memberLabel(id, currentUserId, directory))
                    .join(', ')}
                </span>
              </>
            )}
          </p>
        </div>

        <div className="chat-header-actions">
          {!isGroup ? null : (
            <button
              type="button"
              className="btn btn--icon"
              aria-label="Add people"
              title="Add people"
              onClick={() => setDialog('add')}
            >
              <Icon name="userPlus" size={17} />
            </button>
          )}
          <button
            type="button"
            className="btn btn--icon"
            aria-label={isSaved ? 'Clear saved messages' : isDirect ? 'Delete chat' : 'Leave group'}
            title={isSaved ? 'Clear saved messages' : isDirect ? 'Delete chat' : 'Leave group'}
            onClick={() => setDialog('leave')}
          >
            <Icon name={isSaved ? 'eraser' : isDirect ? 'trash' : 'logOut'} size={17} />
          </button>
        </div>
      </header>

      {dialog === 'add' ? (
        <AddMembersDialog channel={channel} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'leave' ? (
        <DeleteChatDialog channel={channel} title={title} onClose={() => setDialog(null)} />
      ) : null}

      {/*
        Keyed by channel so switching conversations REMOUNTS both.
        That is the whole reset: the transcript's scroll-position latch starts
        fresh at the bottom, and a half-typed message does not follow the user
        into someone else's conversation. Clearing that state from an effect
        instead would mean a render with the wrong channel's state on screen.

        The prefixes are NOT decoration. These two are siblings, and React
        indexes a parent's children by key: give both the bare `channelId` and
        the map holds only one of them, so on the next channel the other subtree
        is never matched and never unmounted. It stays in the DOM, the column
        splits between the old transcript and the new one, and every further
        switch strands another. A key only has to be unique among siblings, so
        each one names what it identifies.
      */}
      <MessageList
        key={`transcript:${channelId}`}
        channelId={channelId}
        memberIds={channel.memberIds}
        transcript={transcript ?? EMPTY_TRANSCRIPT}
        pending={pending}
        showAuthors={isGroup}
      />

      <UploadTray channelId={channelId} />

      <MessageComposer key={`composer:${channelId}`} channelId={channelId}>
        <AttachButton channelId={channelId} />
      </MessageComposer>
    </section>
  );
}

/** Presence never asserts "offline" from an absent value - see `presenceOf`. */
function presenceLabel(status: PresenceStatus | null): string {
  if (status === 'online') {
    return 'Online';
  }

  if (status === 'away') {
    return 'Away';
  }

  return status === 'offline' ? 'Offline' : 'Presence unknown';
}

/**
 * Browsers name every pasted screenshot `image.png`. Give it a timestamped name
 * so a conversation with several of them stays readable.
 */
function namePasted(file: File): File {
  if (!/^image\.(png|jpe?g|gif|webp)$/i.test(file.name)) {
    return file;
  }

  const stamp = new Date().toISOString().slice(0, 19).replace('T', ' ').replace(/:/g, '-');
  const extension = file.name.split('.').pop() ?? 'png';

  return new File([file], `Screenshot ${stamp}.${extension}`, { type: file.type });
}
